import React, { Dispatch, SetStateAction, useState } from 'react';
import { Loader, Pagination } from '@thinkific/toga-react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames/bind';
import { getTotalPages } from 'utils/paginationHelpers';

import UserAvatar from 'components/UserAvatar';
import { LiveEvent_site_liveEvent_interestedUsers_edges as LiveEventUserEdges } from 'schema/Event/__generated__/LiveEvent';
import { NUMBER_OF_INTERESTED_USERS_ON_EVENT } from 'utils/constants';

import styles from './EventInterestedUsersList.module.scss';

const cx = classNames.bind(styles);

interface EventInterestedUsersListProps {
  interestedUsersEdges?: (LiveEventUserEdges | null)[] | null | undefined;
  totalInterestedUsers?: number;
  setEndCursor: Dispatch<SetStateAction<string>>;
  isLoading: boolean;
}

const EventInterestedUsersList: React.FC<EventInterestedUsersListProps> = ({
  totalInterestedUsers,
  interestedUsersEdges,
  setEndCursor,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [userPage, setUserPage] = useState(1);

  const totalUserPages = getTotalPages(
    totalInterestedUsers || 0,
    NUMBER_OF_INTERESTED_USERS_ON_EVENT
  );

  const goToPage = (page: number) => {
    const after = btoa(`${(page - 1) * NUMBER_OF_INTERESTED_USERS_ON_EVENT}`);
    setEndCursor(after);
    setUserPage(page);
  };

  return (
    <>
      {isLoading && (
        <Loader accessibilityLabel={t('common-loading', 'Loading')} />
      )}
      <div className={cx('event-interested-users-list__interested-users')}>
        <span>
          {t('common-interested', 'Interested ({{total}})', {
            total: totalInterestedUsers,
          })}
        </span>
        <div>
          {interestedUsersEdges?.map((edge) => {
            const user = edge?.node;

            if (user) {
              return (
                <div
                  className={cx('event-interested-users-list__interested-user')}
                  key={user.id}
                >
                  <UserAvatar
                    avatarUrl={user.profile?.avatarUrl}
                    className={cx(
                      'event-interested-users-list__interested-user-avatar'
                    )}
                    size="xs"
                    srcSet={`${user.profile?.avatarUrl}?width=40 1x, ${user.profile?.avatarUrl}?width=40&dpr=2 2x, ${user.profile?.avatarUrl}?width=40&dpr=3 3x`}
                    userId={user.id}
                    username={user.fullName || ''}
                    shouldOpenProfile
                  />
                  <span title={user.fullName || ''}>{user.fullName}</span>
                </div>
              );
            }

            return null;
          })}
        </div>
        {totalUserPages > 1 && (
          <Pagination
            className={cx(
              'event-interested-users-list__interested-users-pagination'
            )}
            currentPage={userPage}
            goTofirstHandler={() => goToPage(1)}
            goTolastHandler={() => goToPage(totalUserPages)}
            goToPageHandler={goToPage}
            nextHandler={() => goToPage(userPage + 1)}
            previousHandler={() => goToPage(userPage - 1)}
            totalPages={totalUserPages}
          />
        )}
      </div>
    </>
  );
};

export default EventInterestedUsersList;
